import { UserRole } from '@lombok/shared';
import type { UserProfile } from '@lombok/shared';

/**
 * Pure, dependency-free helpers for turning a Cognito **ID token** into the
 * session profile shape the UI shell uses. These run in the browser only to
 * *bootstrap* the session and drive UX (which portal to show); they are NOT a
 * security boundary. The token is NOT verified here — the AWS backend verifies
 * the signature on every request and is the sole authority on access. We only
 * read the (already-issued) claims to pick a landing portal and pre-fill names.
 *
 * Source-of-truth precedence at runtime: `GET /me` (verified server-side) wins;
 * these claims are the fast bootstrap shown until `/me` resolves.
 */

/** The subset of UserProfile the session carries (mirrors the dev mock shape + relationship links). */
export type SessionUser = Pick<
  UserProfile,
  'userId' | 'email' | 'fullName' | 'role' | 'locale'
> & {
  /** Relationship links from custom claims — only the role-relevant one is set. */
  customerId?: string;
  distributorId?: string;
  salesRepId?: string;
};

/** Raw claim keys we read, kept in one place so the contract with the backend is explicit. */
const CLAIM = {
  sub: 'sub',
  email: 'email',
  name: 'name',
  role: 'custom:role',
  groups: 'cognito:groups',
  locale: 'locale',
  customerId: 'custom:customerId',
  distributorId: 'custom:distributorId',
  salesRepId: 'custom:salesRepId',
} as const;

const VALID_ROLES = new Set<string>(Object.values(UserRole));

/**
 * Decode the payload of a JWT (`header.payload.signature`) as base64url JSON.
 * Returns `null` for any malformed input. This does NOT verify the signature —
 * decoding ≠ trusting. Browser-safe: uses `atob` after base64url→base64.
 */
export function decodeJwtPayload(jwt: string | null | undefined): Record<string, unknown> | null {
  if (!jwt) return null;
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1];
  if (!payload) return null;
  try {
    // base64url → base64, then pad to a multiple of 4.
    let b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    b64 += '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
    // Decode any non-ASCII (e.g. accented names) correctly from the binary string.
    const decoded = decodeURIComponent(
      Array.prototype.map
        .call(json, (c: string) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    const parsed = JSON.parse(decoded) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Resolve the role from claims. Prefers the explicit `custom:role`; falls back
 * to the first valid value in `cognito:groups` (string or array). Returns
 * `undefined` if neither yields a role this app knows — callers decide what to
 * do (the UI treats an unknown role as "sin acceso").
 */
export function roleFromClaims(claims: Record<string, unknown>): UserRole | undefined {
  const explicit = asString(claims[CLAIM.role]);
  if (explicit && VALID_ROLES.has(explicit)) return explicit as UserRole;

  const groups = claims[CLAIM.groups];
  const list: string[] = Array.isArray(groups)
    ? groups.filter((g): g is string => typeof g === 'string')
    : typeof groups === 'string'
      ? groups.split(/[\s,]+/).filter(Boolean)
      : [];
  for (const g of list) {
    if (VALID_ROLES.has(g)) return g as UserRole;
  }
  return undefined;
}

/**
 * Map a decoded ID-token payload to a {@link SessionUser}. Returns `null` when
 * the payload is missing a usable role (the only field with no sensible
 * default). `email`/`name` degrade gracefully so the shell never shows blanks.
 */
export function sessionUserFromClaims(claims: Record<string, unknown> | null): SessionUser | null {
  if (!claims) return null;
  const role = roleFromClaims(claims);
  if (!role) return null;

  const email = asString(claims[CLAIM.email]) ?? '';
  const fullName =
    asString(claims[CLAIM.name]) ?? (email ? (email.split('@')[0] ?? 'Usuario') : 'Usuario');

  return {
    userId: asString(claims[CLAIM.sub]) ?? `usr_${role}`,
    email,
    fullName,
    role,
    locale: asString(claims[CLAIM.locale]) ?? 'es-VE',
    customerId: asString(claims[CLAIM.customerId]),
    distributorId: asString(claims[CLAIM.distributorId]),
    salesRepId: asString(claims[CLAIM.salesRepId]),
  };
}

/** Convenience: decode an ID-token string straight into a {@link SessionUser}. */
export function sessionUserFromIdToken(idToken: string | null | undefined): SessionUser | null {
  return sessionUserFromClaims(decodeJwtPayload(idToken));
}
